import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Star, CheckCircle2 } from "lucide-react";
import { tradeLabel } from "@/components/TradeCategoryPicker";

export default function TradeProfile() {
  const { id } = useParams();
  const [trade, setTrade] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/public/trades/${id}`).then(r => setTrade(r.data)).catch(() => setTrade(false));
  }, [id]);

  // Inject JSON-LD for Google rich snippets (LocalBusiness + AggregateRating + Reviews)
  useEffect(() => {
    if (!trade || trade === false) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${window.location.origin}/trades/${trade.id}`,
      "name": `${trade.name} · Olivia Does ${tradeLabel(trade)}`,
      "description": `${tradeLabel(trade)} specialist in the Olivia Does Partner Network — vetted, insured, reference-checked.`,
      "url": `${window.location.origin}/trades/${trade.id}`,
      "image": "https://images.unsplash.com/photo-1676989859871-150f3579862a",
      "brand": { "@type": "Brand", "name": "Olivia Does" },
      "parentOrganization": { "@type": "Organization", "name": "Olivia Does", "url": window.location.origin },
      "areaServed": { "@type": "Country", "name": "United Kingdom" },
      "knowsAbout": trade.trade_categories?.length ? trade.trade_categories : trade.trade_category,
    };
    if (trade.avg_rating && trade.review_count > 0) {
      ld.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": trade.avg_rating,
        "reviewCount": trade.review_count,
        "bestRating": 5,
        "worstRating": 1,
      };
    }
    if (Array.isArray(trade.recent_reviews) && trade.recent_reviews.length > 0) {
      ld.review = trade.recent_reviews.slice(0, 5).map(r => ({
        "@type": "Review",
        "author": { "@type": "Person", "name": r.customer_name || "Olivia customer" },
        "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": 5, "worstRating": 1 },
        "reviewBody": r.comment || "",
        "datePublished": r.created_at,
      }));
    }
    const tagId = "od-jsonld-trade";
    let el = document.getElementById(tagId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = tagId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(ld);
    document.title = `${trade.name} — ${tradeLabel(trade)} · Olivia Does`;
    return () => { el && el.remove(); };
  }, [trade]);

  if (trade === null) return <div className="text-center py-24 text-[#78716C]">Loading…</div>;
  if (trade === false) return <div className="text-center py-24">Trade not found.</div>;

  return (
    <div data-testid="trade-profile-page" className="max-w-5xl mx-auto px-6 md:px-8 py-16">
      <Link to="/trades" className="od-link text-sm">← All trades</Link>
      <div className="od-marketing-card p-10 mt-6 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <span className="od-label">Olivia partner trade</span>
          <h1 className="font-display text-5xl mt-2" data-testid="trade-name">{trade.name}</h1>
          <p className="text-[#78716C] mt-2">{tradeLabel(trade)} specialist · Partner since {new Date(trade.created_at).getFullYear()}</p>
          <div className="flex gap-6 mt-8">
            <div>
              <p className="od-label">Rating</p>
              {trade.review_count > 0 ? (
                <>
                  <p className="font-display text-3xl mt-1 flex items-center gap-2" data-testid="trade-rating-value">
                    {trade.avg_rating}
                    <Star className="w-5 h-5 fill-[#C77F8C] text-[#C77F8C]" />
                  </p>
                  <p className="text-xs text-[#78716C]">{trade.review_count} review{trade.review_count === 1 ? "" : "s"}</p>
                </>
              ) : (
                <p className="mt-2" data-testid="trade-rating-new">
                  <span className="inline-flex items-center rounded-full bg-[#EAF2EC] text-[#3F6B4E] px-3 py-1 text-sm font-medium">New partner</span>
                </p>
              )}
            </div>
            <div>
              <p className="od-label">Jobs completed</p>
              <p className="font-display text-3xl mt-1">{trade.completed_jobs}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#2A2624] text-white rounded-2xl p-6">
          <CheckCircle2 className="w-6 h-6 text-[#C77F8C]" />
          <p className="font-display text-xl mt-3">Vetted by Olivia</p>
          <p className="text-sm text-white/70 mt-2">Reference-checked, insured and quality-reviewed after every job.</p>
        </div>
      </div>

      <h2 className="font-display text-3xl mt-12">Recent reviews</h2>
      {(!trade.recent_reviews || trade.recent_reviews.length === 0) && <p className="text-[#78716C] mt-4">No reviews yet.</p>}
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {(trade.recent_reviews || []).map(r => (
          <div key={r.id} data-testid={`review-${r.id}`} className="od-card p-6">
            <div className="flex gap-1">
              {[1,2,3,4,5].map(n => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-[#C77F8C] text-[#C77F8C]" : "text-[#E2DBD6]"}`} />)}
            </div>
            <p className="mt-3 text-[#2A2624]">{r.comment || "Great service."}</p>
            <p className="text-xs text-[#78716C] mt-3">— {r.customer_name}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
